// src/components/InternalGoalListView.js
import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useNavigate, useParams } from 'react-router-dom';
import DetailedListView from './DetailedListView';
import EisenhowerMatrixView from './EisenhowerMatrixView';
import { departmentQueries } from '../repository/graphQueries';
import { formatDateTime, formatScore } from '../utils';
import { RiInformationLine as InformationCircleIcon } from 'react-icons/ri';
import { DRI_EXPLANATION } from '../constants';

const goalColumns = [
    { header: 'Name', key: 'name' },
    { header: 'DRI', key: 'assigned_to'},
    { header: 'Status', key: 'status' },
    { header: 'Initiatives', key: 'initiativeCount', align: 'center' },
    { header: 'Blockers', key: 'blockingIssuesCount', align: 'center'},
    { header: 'Score', key: 'score', align: 'center', formatter: formatScore },
    { header: 'Target Date', key: 'targetDate', formatter: formatDateTime },
    { header: 'Description', key: 'description', type: 'longText' },
];

const initiativeColumns = [
    { header: 'Name', key: 'name'},
    { header: 'DRI', key: 'assigned_to'},
    { header: 'Status', key: 'status'},
    { header: 'Supported Goals', key: 'supportedGoals', formatter: val => Array.isArray(val) ? val.join(', ') : val},
    { header: 'Blockers', key: 'blockingIssuesCount', align: 'center'},
    { header: 'Action Items', key: 'actionItemCount', align: 'center'},
    { header: 'Score', key: 'score', align: 'center', formatter: formatScore },
    { header: 'Description', key: 'description', type: 'longText'},
];

const DropdownCheckbox = ({ options, selectedOptions, onChange, title }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div className="relative inline-block text-left">
            <div>
                <button
                    type="button"
                    className="inline-flex justify-center w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none"
                    onClick={() => setIsOpen(!isOpen)}
                >
                    {title}
                    <svg className="-mr-1 ml-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                </button>
            </div>

            {isOpen && (
                <div className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                    <div className="py-1" role="menu" aria-orientation="vertical">
                        {options.map(option => (
                            <label key={option} className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                                <input
                                    type="checkbox"
                                    className="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                                    checked={selectedOptions.includes(option)}
                                    onChange={() => onChange(option)}
                                />
                                <span className="ml-3">{option}</span>
                            </label>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// Tooltip component (can be a shared component or defined locally if not already shared)
const Tooltip = ({ content, children }) => {
    const [isVisible, setIsVisible] = useState(false);
    
    return (
        <div className="relative inline-block">
            <div
                className="inline-flex items-center cursor-help"
                onMouseEnter={() => setIsVisible(true)}
                onMouseLeave={() => setIsVisible(false)}
                onClick={() => setIsVisible(!isVisible)} // For mobile support
            >
                {children}
            </div>
            {isVisible && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-72 p-3 text-sm bg-gray-800 text-white rounded-md shadow-lg z-50">
                    {content}
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 w-0 h-0 border-x-4 border-x-transparent border-b-4 border-b-gray-800"></div>
                </div>
            )}
        </div>
    );
};

const InternalGoalListView = () => {
    const { neo4jService, departmentId: departmentIdFromContext, dateRange } = useOutletContext();
    const navigate = useNavigate();
    const params = useParams();
    
    const activeRouteDepartmentId = departmentIdFromContext || params.departmentId;
    
    const [departments, setDepartments] = useState([]);
    const [selectedDepartment, setSelectedDepartment] = useState(activeRouteDepartmentId || '');
    const [loadingDepartments, setLoadingDepartments] = useState(true);

    const [goalsList, setGoalsList] = useState([]);
    const [loadingGoals, setLoadingGoals] = useState(false);
    const [errorGoals, setErrorGoals] = useState(null);

    const [initiativesList, setInitiativesList] = useState([]);
    const [loadingInitiatives, setLoadingInitiatives] = useState(false);
    const [errorInitiatives, setErrorInitiatives] = useState(null);

    const [dris, setDris] = useState([]);
    const [loadingDris, setLoadingDris] = useState(true);
    const [selectedDris, setSelectedDris] = useState([]);

    const driTooltipContent = DRI_EXPLANATION;

    const loadDris = useCallback(async () => {
        if (!neo4jService || !selectedDepartment) return;
        setLoadingDris(true);
        try {
            const driResult = await neo4jService.runQuery(departmentQueries.getDepartmentDRIs, { departmentId: selectedDepartment });
            setDris(driResult.map(r => r.dri));
        } catch (err) {
            console.error('Error loading DRIs:', err);
        } finally {
            setLoadingDris(false);
        }
    }, [neo4jService, selectedDepartment]);

    useEffect(() => {
        if (selectedDepartment) {
            loadDris();
        }
    }, [selectedDepartment, loadDris]);

    const handleDriChange = (dri) => {
        setSelectedDris(prev =>
            prev.includes(dri) ? prev.filter(d => d !== dri) : [...prev, dri]
        );
    };

    // Load departments
    const loadDepartments = useCallback(async () => {
        if (!neo4jService) return;
        setLoadingDepartments(true);
        try {
            const deptResult = await neo4jService.runQuery(departmentQueries.listAllDepartments);
            const deptList = deptResult.map(r => r.department).filter(d => d);
            setDepartments(deptList);
            
            if (!selectedDepartment && activeRouteDepartmentId && deptList.includes(activeRouteDepartmentId)) {
                setSelectedDepartment(activeRouteDepartmentId);
            }
        } catch (err) {
            console.error('Error loading departments:', err);
        } finally {
            setLoadingDepartments(false);
        }
    }, [neo4jService, selectedDepartment, activeRouteDepartmentId]);

    useEffect(() => {
        loadDepartments();
    }, [loadDepartments]);

    useEffect(() => {
        if (activeRouteDepartmentId && activeRouteDepartmentId !== selectedDepartment) {
            setSelectedDepartment(activeRouteDepartmentId);
        }
    }, [activeRouteDepartmentId, selectedDepartment]);

    const handleDepartmentChange = (event) => {
        const newDept = event.target.value;
        navigate(`/internal/${newDept}/goals`);
    };

    const loadDepartmentGoals = useCallback(async () => {
        if (!neo4jService || !selectedDepartment || !dateRange || !dateRange.start || !dateRange.end) return;
        setLoadingGoals(true); setErrorGoals(null);
        try {
            const params = { 
                departmentId: selectedDepartment, 
                startDate: dateRange.start, 
                endDate: dateRange.end,
                driFilter: selectedDris.length > 0 ? selectedDris : null
            };
            const data = await neo4jService.runQuery(departmentQueries.getDepartmentGoalsList, params);
            setGoalsList(data);
        } catch (e) { setErrorGoals(e.message); setGoalsList([]); }
        finally { setLoadingGoals(false); }
    }, [neo4jService, selectedDepartment, dateRange, selectedDris]);

    const loadDepartmentInitiatives = useCallback(async () => {
        if (!neo4jService || !selectedDepartment || !dateRange || !dateRange.start || !dateRange.end) return;
        setLoadingInitiatives(true); setErrorInitiatives(null);
        try {
             const params = { 
                departmentId: selectedDepartment, 
                startDate: dateRange.start, 
                endDate: dateRange.end,
                driFilter: selectedDris.length > 0 ? selectedDris : null
            };
            const data = await neo4jService.runQuery(departmentQueries.getDepartmentInitiativesList, params);
            setInitiativesList(data);
        } catch (e) { setErrorInitiatives(e.message); setInitiativesList([]); }
        finally { setLoadingInitiatives(false); }
    }, [neo4jService, selectedDepartment, dateRange, selectedDris]);

    useEffect(() => {
        if (selectedDepartment) {
            loadDepartmentGoals();
            loadDepartmentInitiatives();
        }
    }, [selectedDepartment, loadDepartmentGoals, loadDepartmentInitiatives]);
    
    return (
        <>
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">Department Goals & Initiatives</h2>
                {departments.length > 0 && (
                    <div className="flex items-center space-x-2">
                         <div className="flex items-center">
                            <DropdownCheckbox
                                options={dris}
                                selectedOptions={selectedDris}
                                onChange={handleDriChange}
                                title="Filter by DRI"
                            />
                            <Tooltip content={driTooltipContent}>
                                <InformationCircleIcon className="h-4 w-4 text-gray-500 ml-1 cursor-help" />
                            </Tooltip>
                        </div>
                        <label htmlFor="department-select-goals" className="text-sm font-medium text-gray-700">
                            Department:
                        </label>
                        <select
                            id="department-select-goals"
                            value={selectedDepartment}
                            onChange={handleDepartmentChange}
                            className="block w-48 pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                            disabled={loadingDepartments || !departments.length}
                        >
                            {departments.map(dept => (
                                <option key={dept} value={dept}>{dept}</option>
                            ))}
                        </select>
                    </div>
                )}
            </div>
            
            <div className="mb-8">
                <EisenhowerMatrixView 
                    departmentId={selectedDepartment} 
                    clientId={null} 
                    typeFilter={['Goal', 'Initiative']}
                    driFilter={selectedDris}
                />
            </div>
            <DetailedListView
                title="Department Goals"
                items={goalsList}
                columns={goalColumns}
                isLoading={loadingGoals}
                error={errorGoals}
                entityType="goals"
            />
            <DetailedListView
                title="Department Initiatives"
                items={initiativesList}
                columns={initiativeColumns}
                isLoading={loadingInitiatives}
                error={errorInitiatives}
                entityType="initiatives"
            />
        </>
    );
};

export default InternalGoalListView;